import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';

import { DialogService } from '@ngneat/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Vehicle } from 'rest-client';

import { GoogleTagManagerService } from '@app/modules/core/services/analytics/google-tag-manager.service';
import { VehicleService } from '@app/modules/core/services/vehicle/vehicle.service';
import { DIALOG_CONFIG } from '@app/modules/core/utils/dialog.config';
import {
  CarPropulsion,
  CarPropulsionsResponse,
  UmcData,
  UmcPayload,
  VehicleReturned,
} from '@app/modules/shared/models';

import { FeedbackDialogComponent } from '../feedback-dialog/feedback-dialog.component';

/**
 * > Shows UMC(list) selector when <b>selected vehicle</b> have multiple UMC and client needs to choose one.
 *
 * > Shows UMC(list) selector when created Vehicle at ***bkg-vehicle-dialog-license-plate***
 * fails at auto-identify (because have multiple UMC) and client needs to choose one.
 *
 */

@UntilDestroy()
@Component({
  selector: 'bkg-vehicle-dialog-umc-data-selectors',
  templateUrl: './vehicle-dialog-umc-data-selectors.component.html',
})
export class VehicleDialogUmcDataSelectorsComponent implements OnInit {
  @Output() errorReturn = new EventEmitter<HttpErrorResponse>();
  @Output() umcDataSelectorsReturn = new EventEmitter<VehicleReturned>();
  @Output() notFoundCarOptionReturn = new EventEmitter<boolean>();
  @Input() umc: string;
  @Input() umcData: string;
  @Input() vehicleId: number;
  @Input() isLogged: boolean;
  @Input() clientId: number;

  umcDataForm: FormGroup;

  umcSelectorValues: UmcData[];
  propulsions: CarPropulsion[];
  loadingPropulsion: boolean;
  loadingUmcData: boolean;

  constructor(
    private vehicleService: VehicleService,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private dialog: DialogService,
    private googleTagManagerService: GoogleTagManagerService
  ) {}

  ngOnInit(): void {
    this.convertUmcData();
    this.buildUmcDataForm();
    this.subscribeToUmcSelectChanges();
  }

  private convertUmcData = (): void => {
    if(!this.umcData && this.umc) {
      this.getUmcDatabyUmc(this.umc)
    } else {
      this.umcSelectorValues = (JSON.parse(this.umcData) as UmcData[]) || [];
    }
  };

  private buildUmcDataForm = (): void => {
    this.umcDataForm = this.formBuilder.group({
      modelDescriptionSelect: [null, [Validators.required]],
      propulsionCodeSelect: [
        { value: null, disabled: true },
        [Validators.required],
      ],
    });
  };

  private subscribeToUmcSelectChanges(): void {
    const control = this.getControl('modelDescriptionSelect');
    control.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((modelDescription: string) => {
        if (this.getControl('propulsion')) this.resetControl('propulsion');
        this.getCarPropulsionsbyModelDescription(modelDescription);
      });
  }

  private enableControl = (controlName: string): void => {
    this.umcDataForm.controls[controlName].enable();
  };

  private resetControl = (controlName: string): void => {
    this.getControl(controlName).reset();
    this.getControl(controlName).disable();
  };

  private getUmcDatabyUmc = (umc: string): void => {
    this.loadingUmcData = true;
    this.vehicleService
      .getUmcDatabyUmc(umc)
      .pipe(untilDestroyed(this))
      .subscribe(
        ({ umc_data }: any) => {
          //PUSH umc_data as UmcData INTO this.umcSelectorValues ARRAY
          this.umcSelectorValues = [umc_data as UmcData];
          const modelDescriptionSelect= this.getControl('modelDescriptionSelect')
          modelDescriptionSelect.setValue(umc_data.modelDescription);
          modelDescriptionSelect.updateValueAndValidity()
          this.loadingUmcData = false;
        },
        (error: HttpErrorResponse) => {
          this.errorReturn.emit(error);
          this.loadingUmcData = false;
        }
      );
  };

  private getCarPropulsionsbyModelDescription = (modelDesc: string): void => {
    if(!modelDesc) return;
    this.loadingPropulsion = true;
    this.vehicleService
      .getCarPropulsionsbyModelDescription(modelDesc)
      .pipe(untilDestroyed(this))
      .subscribe(
        ({ propulsion_codes }: CarPropulsionsResponse) => {
          this.propulsions = propulsion_codes;
          this.enableControl('propulsionCodeSelect');
          this.loadingPropulsion = false;
        },
        (error: HttpErrorResponse) => {
          this.errorReturn.emit(error);
          this.loadingPropulsion = false;
        }
      );
  };

  sendUmcDataForm(): void {
    const umcPayload = this.createPayload();

    if (this.isLogged) {
      this.setVehicleUmc(this.vehicleId, umcPayload);
    } else {
      this.createNewVehicleWithUmc(umcPayload);
    }
  }

  private createPayload(): UmcPayload {
    const umcPayload: UmcPayload = {};
    const umcData = this.getUmcDatafromModelDescription(
      this.getControlValue('modelDescriptionSelect') as string
    );
    const propulsionCode = this.getControlValue(
      'propulsionCodeSelect'
    ) as string;

    umcPayload.makeCode = umcData.makeCode;
    umcPayload.makeDescription = umcData.makeDescription;
    umcPayload.modelDescription = umcData.modelDescription;
    umcPayload.umc = umcData.umc;
    umcPayload.propulsion_code = propulsionCode;

    return umcPayload;
  }

  private setVehicleUmc = (vehicle_id: number, payload: UmcPayload): void => {
    this.vehicleService
      .setVehicleUmc(vehicle_id, payload)
      .pipe(untilDestroyed(this))
      .subscribe(
        (vehicle: Vehicle) => {
          this.googleTagManagerService.pushOnDataLayer({
            event: 'vehiculo-seleccionado-matricula-masdatos',
            'vehiculo-marca': vehicle?.vehicle_information?.make_description,
            'vehiculo-modelo': vehicle?.vehicle_information?.model_description,
          });
          this.googleTagManagerService.pushBookingActionOnDataLayer({
            event_name: 'vehiculo-seleccionado-matricula-masdatos',
            'vehiculo-marca': vehicle?.vehicle_information?.make_description,
            'vehiculo-modelo': vehicle?.vehicle_information?.model_description,
          });
          this.umcDataSelectorsReturn.emit({
            vehicle: vehicle,
            type: 'umcData',
          });
        },
        (error: HttpErrorResponse) => this.errorReturn.emit(error)
      );
  };

  private createNewVehicleWithUmc = (payload: UmcPayload): void => {
    payload.plate = null;
    if (this.clientId) {
      payload.client_id = this.clientId;
    }
    this.vehicleService
      .createVehicle(payload)
      .pipe(untilDestroyed(this))
      .subscribe(
        (vehicle: Vehicle) => {
          this.openFeedbackDialog(vehicle);
          this.umcDataSelectorsReturn.emit({
            vehicle: vehicle,
            type: 'umcData',
          });
        },
        (error: HttpErrorResponse) => this.errorReturn.emit(error)
      );
  };

  private openFeedbackDialog(vehicle: Vehicle): void {
    return;
    const plate = vehicle?.plate ? vehicle.plate : '';
    const model =
      vehicle?.vehicle_information &&
      vehicle?.vehicle_information.make_description
        ? vehicle?.vehicle_information.make_description +
          ' ' +
          vehicle?.vehicle_information.model_description
        : '';
    this.dialog.open(FeedbackDialogComponent, {
      ...DIALOG_CONFIG,
      size: 'md',
      enableClose: false,
      data: {
        icon: 'assets/images/icons/ok-icon.svg',
        title: this.translateService.instant(
          'sharedModule.bkgFeedbackDialog.vehicleTitle'
        ) as string,
        body: this.translateService.instant(
          'sharedModule.bkgFeedbackDialog.vehicleBody',
          { plate: plate, model: model }
        ) as string,
      },
    });
  }

  onClickNotFoundCarOption = (): void => {
    this.notFoundCarOptionReturn.emit(true);
  };

  private getUmcDatafromModelDescription(modelDescription: string): UmcData {
    return this.umcSelectorValues.find(
      (umcdata: UmcData) => umcdata.modelDescription === modelDescription
    );
  }

  private getControl(controlName: string): AbstractControl {
    return this.umcDataForm.get(controlName);
  }

  private getControlValue(controlName: string): unknown {
    return this.getControl(controlName).value as unknown;
  }

  get isButtonDisabled(): boolean {
    return this.umcDataForm?.invalid;
  }
}
